import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { deleteUser, setAuth } from 'src/app/modules/auth/redux/AuthSlice'
import { logout } from 'src/app/modules/auth/services/firebaseAuthService'
import { setUserOffline } from 'src/app/modules/auth/services/userPresenceService'
import { cleanReduxContact } from 'src/app/modules/contact/redux/ContactSlice'
import { cleanReduxShipment } from 'src/app/modules/product/shipment/redux/ShipmentsSlice'
import { cleanReduxProduct } from 'src/app/modules/product/redux/ProductSlice'
import { cleanReduxMarketplaceProductCategory } from 'src/app/modules/product/marketplaceProductCategory/marketplaceProductCategorySlice'
import { persistor } from 'src/setup/redux/store'
import { cleanReduxTemplate } from 'src/app/modules/template/list/TemplateListSlice'
import { cleanReduxCampaign as cleanReduxCampaignList } from 'src/app/modules/campaign/list/CampaignListSlice'
import { cleanReduxCampaign } from 'src/app/modules/campaign/redux/CampaignSlice'
import { cleanReduxProductList } from 'src/app/modules/product/productList/ProductListSlice'
import { cleanReduxStorefrontList } from 'src/app/modules/product/storefront/storefrontList/StorefrontListSlice'
import * as lc from 'src/app/modules/localstorage'
import { unsubscribeFromTokenChanges } from 'src/db/serviceApiSetting'
import { updateSession } from 'src/app/modules/auth/services/sessionService'

interface LogoutHandlerCallbacks {
  onShowUnsavedChangesModal?: () => void;
  onShowLogoutConfirmationModal?: () => void;
  hasUnsavedChanges?: boolean;
}

export const useLogoutHandler = (callbacks?: LogoutHandlerCallbacks) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Main logout initiation - checks for unsaved changes first
  const logoutHandler = () => {
    if (callbacks?.hasUnsavedChanges) {
      // Show unsaved changes modal first
      callbacks.onShowUnsavedChangesModal?.();
    } else {
      // Show logout confirmation modal
      callbacks.onShowLogoutConfirmationModal?.();
    }
  }

  // Called after unsaved changes are handled - show logout confirmation
  const showLogoutConfirmation = () => {
    callbacks.onShowLogoutConfirmationModal?.();
  }

  // Actual logout execution - called after confirmation
  const executeLogout = () => {
    const currentUser = lc.getItemLC(lc.LCName.User)
    const sessionid = lc.getItemLC(lc.LCName.SessionID)

    if (currentUser === null || sessionid === null) {
      dispatch(setAuth(false))
      navigate('/auth')
    } else {
      logout()
        .then(() => {
          unsubscribeFromTokenChanges()
          setUserOffline(currentUser.uid, sessionid)

          // Mark session as ended (fire-and-forget — don't block logout)
          const sessionCreated = lc.getItemLC(lc.LCName.SessionCreated)
          if (sessionCreated) {
            updateSession(currentUser.uid, sessionid, sessionCreated).catch(() => {})
          }

          dispatch(deleteUser())
          lc.removeSession()
          dispatch(setAuth(false))
          
          // Clean all Redux stores
          dispatch(cleanReduxContact(true))
          dispatch(cleanReduxShipment(true))
          dispatch(cleanReduxProduct(true))
          dispatch(cleanReduxMarketplaceProductCategory(true))
          dispatch(deleteUser())
          dispatch(cleanReduxContact(true))
          dispatch(cleanReduxTemplate(true))
          dispatch(cleanReduxCampaign(true))
          dispatch(cleanReduxProductList(true))
          dispatch(cleanReduxStorefrontList(true))
          dispatch(cleanReduxCampaignList(true))
          
          // Clear persisted data
          persistor.pause()
          persistor.flush().then(() => {
            return persistor.purge()
          })
          
          navigate('/auth')
        })
        .catch((error) => {
          console.log('failed logout', error)
        })
    }
  }

  return { 
    logoutHandler, 
    showLogoutConfirmation, 
    executeLogout 
  }
}
